import { Actors as A, createAuthorizeAdvertiserIdTransform, Tenants as T } from '@mntn/auth-types'
import { QueryIDSchema } from '@mntn/zod'
import { z } from 'zod'

const CampaignGroupPublicStatusSchema = z.union([
  z.literal('ACTION_REQ'),
  z.literal('ARCHIVED'),
  z.literal('ENDED'),
  z.literal('INCOMPLETE'),
  z.literal('LEGACY_ARCHIVED'),
  z.literal('LIVE'),
  z.literal('PAUSED'),
  z.literal('READY'),
  z.literal('SCHEDULED'),
])

export const CampaignGroupSchema = z.object({
  campaignGroupId: z.number(),
  advertiserId: z.number(),
  name: z.string(),
  effectiveBudget: z.number().nullable(),
  ineffectiveBudget: z.number().nullable(),
  totalBudget: z.number().nullable(),
  spend: z.number().nullable(),
  startTime: z.string().nullable(),
  endTime: z.string().nullable(),
  status: CampaignGroupPublicStatusSchema,
})

export const GetCampaignGroupByIdResponseSchema = z.object({
  data: CampaignGroupSchema,
})

export const GetCampaignGroupsResponseSchema = z.object({
  data: z.object({
    count: z.number(),
    rows: z.array(CampaignGroupSchema),
  }),
})

export const GetCampaignGroupsStatusSummaryResponseSchema = z.object({
  data: z.object({
    upcoming: z.number().default(0),
    live: z.number().default(0),
    completed: z.number().default(0),
  }),
})

export const GetCampaignGroupsFilterSchema = z.object({
  'advertiser-id': z.array(z.number()).optional(),
  'campaign-group-id': z.array(z.number()).optional(),
})

const GetCampaignGroupsSortOrderSchema = z.union([z.literal('ASC'), z.literal('DESC')])

const FindCampaignGroupsStatusSchema = z.union([
  z.literal('ARCHIVED'),
  z.literal('ENDED'),
  z.literal('INCOMPLETE'),
  z.literal('LEGACY_ARCHIVED'),
  z.literal('LIVE'),
  z.literal('PAUSED'),
  z.literal('READY'),
  z.literal('SCHEDULED'),
])

export const GetCampaignGroupByIdParamsSchema = z.object({
  campaignGroupId: QueryIDSchema,
})

export const GetCampaignGroupsQuerySchema = z.object({
  page: z.number().optional(),
  limit: z.number().optional(),
  'sort-by': z.string().optional(),
  'sort-order': GetCampaignGroupsSortOrderSchema.optional(),
  filter: GetCampaignGroupsFilterSchema.optional(),
})

export const FindCampaignGroupsQuerySchema = z.object({
  'advertiser-id': QueryIDSchema,
  statuses: z.array(FindCampaignGroupsStatusSchema).optional(),
  search: z.string().optional(),
  page: z.number().optional(),
  limit: z.number().optional(),
})

export const FindCampaignGroupsRequestSchema = z
  .object({
    query: FindCampaignGroupsQuerySchema,
    principal: T.LoginAsSchema('admin').and(A.UserSchema),
  })
  .transform(
    createAuthorizeAdvertiserIdTransform(
      request => request.query['advertiser-id'],
      (request, advertiserId) => ({
        ...request,
        query: { ...request.query, 'advertiser-id': advertiserId },
      }),
    ),
  )

export const FindCampaignGroupsResponseSchema = z.object({
  data: z.object({
    count: z.number(),
    rows: z.array(CampaignGroupSchema.pick({ campaignGroupId: true, name: true })),
  }),
})

export type CampaignGroup = z.infer<typeof CampaignGroupSchema>
export type GetCampaignGroupByIdResponse = z.infer<typeof GetCampaignGroupByIdResponseSchema>
export type GetCampaignGroupsResponse = z.infer<typeof GetCampaignGroupsResponseSchema>
export type GetCampaignGroupsStatusSummaryResponse = z.infer<typeof GetCampaignGroupsStatusSummaryResponseSchema>
export type CampaignGroupFilterInput = z.infer<typeof GetCampaignGroupsFilterSchema>

export const GetCampaignGroupByIdRequestSchema = z.object({
  params: z.custom<z.output<typeof GetCampaignGroupByIdParamsSchema>>(),
  principal: T.KindSchema(['advertiser', 'none']).and(A.UserSchema),
})

export const GetCampaignGroupsRequestSchema = z.object({
  query: z.custom<z.output<typeof GetCampaignGroupsQuerySchema>>(),
  principal: T.KindSchema(['advertiser', 'none']).and(A.UserSchema),
})

export const GetCampaignGroupsStatusSummaryRequestSchema = z.object({
  query: z.custom<z.output<typeof GetCampaignGroupsQuerySchema>>(),
  principal: T.KindSchema(['advertiser', 'none']).and(A.UserSchema),
})
